import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard/Dashboard';
import AddProject from './components/AddProject';
import GetCase from './components/GetCase';
import CallbackOutlookApiPage from './components/CallbackOutlookApi';
import GlobalSetting from './components/GlobalSetting';
import LoginPage from './components/LoginPage';
import AuthRoute from './components/AuthRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ReadOnlyProjectDashboard from './components/ReadOnlyProjectDashboard';
import { Box } from '@mui/material';
import { useWebSocket } from './components/WebSocketContext';
import { useUser } from './components/WebSocketContext';
import DisconnectedOverlay from './components/DisconnectOverlay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import StatusBar from './components/StatusBar';
import SoftwareCanBeUpdated from './components/SoftwareCanBeUpdated';
import MfaSetupDialog from './components/MfaSetupDialog';
import PurchaseCredits from './components/PurchaseCredits';

function HomeRoute() {
  const { permissions, isReadOnly } = useUser();

  if (!permissions) {
    return null;
  }

  if (isReadOnly) {
    return <Navigate to="/project-dashboard" replace />;
  }

  return (
    <ProtectedRoute>
      <AddProject />
    </ProtectedRoute>
  );
}

function RouteAccessModeSync() {
  const location = useLocation();
  const { setPublicRouteActive } = useWebSocket();

  useEffect(() => {
    setPublicRouteActive(location.pathname.startsWith('/public/case/'));
  }, [location.pathname, setPublicRouteActive]);

  return null;
}

function App() {
  const { isConnected } = useWebSocket();

  const animationStyle = `
    @keyframes scaleAnimation {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.2);
      }
    }
  `;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <RouteAccessModeSync />
        <StatusBar />
        <SoftwareCanBeUpdated />
        <MfaSetupDialog />

        <Routes>
          {/* Login route - accessible without authentication */}
          <Route path="/login" element={<LoginPage />} />

          {/* Public case route - accessible without authentication */}
          <Route path="/public/case/:caseid" element={
            <div style={{ display: 'flex', position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
              <DisconnectedOverlay isConnected={isConnected} />
              <Box flexGrow={1} sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                <Dashboard />
              </Box>
            </div>
          } />

          {/* Protected routes */}
          <Route path="/*" element={
            <AuthRoute>
              <div style={{ display: 'flex', position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
                <DisconnectedOverlay isConnected={isConnected} />

                <Sidebar />
                <Box flexGrow={1} sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                  <style>
                    {animationStyle}
                  </style>
                  <Routes>
                    <Route path="/case/:caseid" element={<Dashboard />} />
                    <Route path="/add" element={
                      <ProtectedRoute>
                        <AddProject />
                      </ProtectedRoute>
                    } />
                    <Route path="/purchase-credits" element={
                      <PurchaseCredits />
                    } />
                    <Route path="/get" element={<GetCase />} />
                    <Route path="/Settings" element={
                      <ProtectedRoute allowReadOnly>
                        <GlobalSetting />
                      </ProtectedRoute>
                    } />
                    <Route path="/callback" element={<CallbackOutlookApiPage />} />
                    <Route path="/project-dashboard" element={<ReadOnlyProjectDashboard />} />
                    <Route path="/home-denied" element={<Navigate to="/project-dashboard" replace />} />
                    <Route path="/" element={<HomeRoute />} />
                  </Routes>
                </Box>
              </div>
            </AuthRoute>
          } />
        </Routes>
      </Router>
    </LocalizationProvider>
  );
}

export default App;
